import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Box, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import supabase from "../../../../../utils/supabase";
import DialogScientificNameEditor from "../../Dialogs/DialogScientificNameEditor";

export default function ChildrenListDialog({ open, onClose, parentId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const fetchChildren = useCallback(async () => {
    if (!parentId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("scientific_names")
        .select("id, name_spell_valid, name_spell_original, current_rank, authority_year, gbif_id")
        .eq("current_parent", parentId)
        .order("name_spell_valid", { ascending: true });
      if (error) {
        console.error("[ChildrenListDialog] fetch error", error);
        setRows([]);
      } else {
          const list = Array.isArray(data) ? data : [];
          if (list.length === 0) {
            // Fallback: sometimes current_parent may be stored as different form (GBIF key or prefixed);
            // try a looser match using ilike to help surface children in mixed datasets.
            try {
              const like = `%${String(parentId).replace(/%/g, "\\%").replace(/,/g, "\\,")}%`;
              const { data: data2, error: err2 } = await supabase
                .from("scientific_names")
                .select("id, name_spell_valid, name_spell_original, current_rank, authority_year, gbif_id")
                .or(`current_parent.ilike.${like}`)
                .order("name_spell_valid", { ascending: true });
              if (!err2 && Array.isArray(data2) && data2.length > 0) {
                setRows(data2);
              } else {
                setRows(list);
              }
            } catch (e2) {
              console.error("[ChildrenListDialog] fallback fetch error", e2);
              setRows(list);
            }
          } else {
            setRows(list);
          }
      }
    } catch (e) {
      console.error("[ChildrenListDialog] fetch catch", e);
      setRows([]);
    }
    setLoading(false);
  }, [parentId]);

  useEffect(() => {
    if (open) fetchChildren();
  }, [open, fetchChildren]);

  const handleDelete = useCallback(
    async (id) => {
      if (!id) return;
      if (!window.confirm("Delete this child? This action cannot be undone.")) return;
      try {
        const { error } = await supabase.from("scientific_names").delete().eq("id", id);
        if (error) throw error;
        setRows((r) => r.filter((x) => x.id !== id));
      } catch (e) {
        console.error("[ChildrenListDialog] delete error", e);
        alert("Delete failed: " + (e.message || e));
      }
    },
    []
  );

  const columns = [
    { field: "id", headerName: "ID", width: 200 },
    { field: "name_spell_valid", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "current_rank", headerName: "Rank", width: 120 },
    { field: "authority_year", headerName: "Year", width: 100 },
    { field: "gbif_id", headerName: "GBIF", width: 140 },
    {
      field: "__actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => setEditRow(p.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(p.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Children of {parentId}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={rows.map((r) => ({ ...r, id: String(r.id) }))}
              columns={columns}
              loading={loading}
              pageSize={25}
              rowsPerPageOptions={[25, 50, 100]}
              disableSelectionOnClick
              getRowId={(row) => row.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {editRow && (
        <DialogScientificNameEditor
          open
          mode="edit"
          scientificName={editRow}
          onClose={(saved) => {
            setEditRow(null);
            if (saved) fetchChildren();
          }}
          localMode
        />
      )}
    </>
  );
}
